import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MainCard from 'components/MainCard';
import { useSubStores } from 'contexts/SubStoreContext';
import { useEmployees } from 'contexts/EmployeeContext';
import { calculateShiftTimings, formatTimeForDisplay } from 'utils/shiftTimingCalculator';
import { fetchSubstoreDetails } from 'api/substoreDetails';
import { patchSubstoreDetails } from 'api/substoreEdit';

export default function SubstoreDetails() {
  const { substoreId } = useParams();
  const navigate = useNavigate();
  const { substores, updateSubStore } = useSubStores();
  const { getEmployeesBySubstore, getEmployeesWithShiftInfo } = useEmployees();
  const [substore, setSubstore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [editedSubstore, setEditedSubstore] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('auto');
  const [customShiftTimings, setCustomShiftTimings] = useState([]);
  const [useCustomTimings, setUseCustomTimings] = useState(false);

  // Fetch specific substore details from backend using ID
  useEffect(() => {
    const getSubstoreDetails = async () => {
      if (!substoreId) {
        navigate('/substores');
        return;
      }

      setLoading(true);
      try {
        // Fetch substore details from API
        const substoreData = await fetchSubstoreDetails(substoreId);
        console.log('Fetched substore details:', substoreData);

        // Set the substore data in state
        setSubstore({
          id: substoreData.id,
          storeName: substoreData.name,
          address: substoreData.address,
          workingTimeStart: substoreData.working_time_start || '09:00',
          workingTimeEnd: substoreData.working_time_end || '17:00',
          joinCode: substoreData.join_code
        });

        setError(null);
      } catch (err) {
        console.error('Error fetching substore details:', err);
        setError('Failed to load substore details. Please try again later.');
        // Fallback to context data if API fails
        if (substores.length > 0) {
          const contextSubstore = substores.find((s) => s.id.toString() === substoreId.toString());
          if (contextSubstore) {
            setSubstore(contextSubstore);
            setError(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    getSubstoreDetails();
  }, [substoreId, substores, navigate]);

  const handleViewEmployees = () => {
    // Navigate to employees page with the substore pre-selected
    navigate('/employees', { state: { selectedSubstore: parseInt(substoreId) } });
  };

  const handleBackToSubstores = () => {
    navigate('/substores');
  };

  // Open edit dialog for substore details (address, working hours, shifts)
  const handleEditShifts = () => {
    setEditedSubstore({ ...substore });
    setUseCustomTimings(false);
    setCustomShiftTimings([]);
    setEditDialogOpen(true);
  };

  // Save changes to backend using PATCH
  const handleSaveShifts = async () => {
    if (editedSubstore) {
      try {
        // Prepare data for PATCH (only send editable fields)
        const patchData = {
          name: editedSubstore.storeName,
          address: editedSubstore.address,
          working_time_start: editedSubstore.workingTimeStart,
          working_time_end: editedSubstore.workingTimeEnd,
        };
        // PATCH request to backend
        const updated = await patchSubstoreDetails(substoreId, patchData);
        // Update local state/UI
        setSubstore((prev) => ({
          ...prev,
          storeName: updated.name || prev.storeName,
          address: updated.address,
          workingTimeStart: updated.working_time_start,
          workingTimeEnd: updated.working_time_end,
        }));
        setEditDialogOpen(false);
        setEditedSubstore(null);
        setCustomShiftTimings([]);
        setUseCustomTimings(false);
      } catch (err) {
        console.error('Failed to update substore details:', err?.response?.data || err?.message || err);
        const message = (err && err.response && (err.response.data?.error || err.response.data?.detail)) || 'Failed to update substore details.';
        alert(message);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditedSubstore(null);
    setCustomShiftTimings([]);
    setUseCustomTimings(false);
  };

  const handleAssignmentOptions = () => {
    setAssignmentDialogOpen(true);
  };

  const handleAutoAssign = () => {
    // Auto assignment logic will be implemented
    console.log('Auto assigning employees to shifts...');
    setAssignmentDialogOpen(false);
  };

  const handleManualAssign = () => {
    // Manual assignment logic will be implemented
    console.log('Opening manual assignment interface...');
    setAssignmentDialogOpen(false);
  };

  const handleNumShiftsChange = (newNumShifts) => {
    if (editedSubstore) {
      const numShifts = parseInt(newNumShifts) || 1;
      setEditedSubstore((prev) => ({ ...prev, numShifts }));

      // Generate default custom timings if using custom mode
      if (useCustomTimings) {
        const defaultTimings = calculateShiftTimings(editedSubstore.workingTimeStart, editedSubstore.workingTimeEnd, numShifts);
        setCustomShiftTimings(defaultTimings);
      }
    }
  };

  const handleCustomTimingChange = (shiftIndex, field, value) => {
    const updatedTimings = [...customShiftTimings];
    updatedTimings[shiftIndex] = {
      ...updatedTimings[shiftIndex],
      [field]: value
    };
    setCustomShiftTimings(updatedTimings);
  };

  const toggleCustomTimings = () => {
    if (!useCustomTimings) {
      // Switch to custom timings - generate default timings
      const defaultTimings = calculateShiftTimings(
        editedSubstore.workingTimeStart,
        editedSubstore.workingTimeEnd,
        parseInt(editedSubstore.numShifts)
      );
      setCustomShiftTimings(defaultTimings);
    }
    setUseCustomTimings(!useCustomTimings);
  };

  if (loading) {
    return (
      <MainCard title="Substore Details">
        <Typography>Loading substore details...</Typography>
      </MainCard>
    );
  }

  if (error && !substore) {
    return (
      <MainCard title="Substore Details">
        <Box>
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToSubstores}>
            Back to Sub Stores
          </Button>
        </Box>
      </MainCard>
    );
  }

  if (!substore) {
    return (
      <MainCard title="Substore Details">
        <Typography>No substore found with ID: {substoreId}</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToSubstores} sx={{ mt: 2 }}>
          Back to Sub Stores
        </Button>
      </MainCard>
    );
  }

  const employees = getEmployeesBySubstore(parseInt(substoreId));
  const employeesWithShiftInfo = getEmployeesWithShiftInfo(parseInt(substoreId), substores);

  // Calculate shift timings - use custom timings if available, otherwise auto-generate
  const shiftTimings =
    substore.customShiftTimings && substore.customShiftTimings.length > 0
      ? substore.customShiftTimings
      : calculateShiftTimings(
          substore.workingTimeStart || '09:00',
          substore.workingTimeEnd || '17:00',
          parseInt(substore.numShifts || '1')
        );

  return (
    <MainCard title="Substore Details">
      <Box mb={2}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToSubstores} sx={{ mb: 2 }}>
          Back to Sub Stores
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 600 }}>
              {substore.storeName || 'Unnamed Store'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditShifts}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                boxShadow: 2
              }}
            >
              Edit
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                  Store Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                      {substore.address || 'No address provided'}
                    </Typography>
                  </Box>
                  {/*<Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Join Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, fontWeight: 'bold' }}>
                      {substore.joinCode || 'N/A'}
                    </Typography>
                  </Box>*/}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Working Hours
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                      {(substore.workingTimeStart || 'Not set')} - {(substore.workingTimeEnd || 'Not set')}
                    </Typography>
                  </Box>
                  {/*<Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Shift Details
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                      {substore.numShifts || '0'}
                    </Typography>
                  </Box>*/}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Employee List
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {/* <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {Array.isArray(employees) ? employees.length : '0'}
                      </Typography> */}
                      <Button variant="contained" size="medium" startIcon={<VisibilityIcon />} onClick={handleViewEmployees}>
                        View
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Shift Timings Section */}
          {/* {shiftTimings.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <ScheduleIcon sx={{ fontSize: '1.5rem' }} />
                        Shift Timings
                      </Typography>
                      <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditShifts} size="small">
                        Edit Shifts
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6, color: 'text.secondary', mb: 2 }}>
                        Working hours are divided into {substore.numShifts} shifts with seamless transitions:
                      </Typography>

                      <Grid container spacing={3}>
                        {shiftTimings.map((shift) => (
                          <Grid item xs={12} sm={6} md={4} key={shift.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                p: 3,
                                minHeight: 150,
                                width: 200,
                                border: '2px solid',
                                borderColor: 'primary.main',
                                backgroundColor: 'primary.50',
                                '&:hover': {
                                  backgroundColor: 'primary.100',
                                  transform: 'translateY(-2px)',
                                  transition: 'all 0.2s ease-in-out'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {shift.name}
                                  </Typography>
                                  <Chip label={shift.duration} size="small" color="primary" variant="outlined" />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    <strong>Start:</strong> {formatTimeForDisplay(shift.startTime)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    <strong>End:</strong> {formatTimeForDisplay(shift.endTime)}
                                  </Typography>
                                </Box>

                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    {shift.startTime} - {shift.endTime}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                        <Typography variant="body2" sx={{ color: 'info.700', fontWeight: 500 }}>
                          💡 <strong>Note:</strong> Each shift ends exactly when the next one begins, ensuring continuous coverage during
                          store hours.
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          startIcon={<PersonAddIcon />}
                          onClick={handleAssignmentOptions}
                          size="large"
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600
                          }}
                        >
                          Assign Employees to Shifts
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )} */}

          {/* Employee Shift Assignments Section */}
          {employeesWithShiftInfo.length > 0 && (
            <>
              <Divider sx={{ my: 4 }} />

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                      Employee Shift Assignments
                    </Typography>

                    <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6, color: 'text.secondary', mb: 3 }}>
                      {employeesWithShiftInfo.length} employees assigned across {substore.numShifts} shifts:
                    </Typography>

                    <Grid container spacing={2}>
                      {shiftTimings.map((shift) => {
                        const employeesInShift = employeesWithShiftInfo.filter((emp) => emp.shiftInfo?.shiftId === shift.id);
                        return (
                          <Grid item xs={12} md={6} key={shift.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                p: 2,
                                border: '2px solid',
                                borderColor: employeesInShift.length > 0 ? 'success.main' : 'grey.300',
                                backgroundColor: employeesInShift.length > 0 ? 'success.50' : 'grey.50'
                              }}
                            >
                              <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {shift.name}
                                  </Typography>
                                  <Chip
                                    label={`${employeesInShift.length} employees`}
                                    size="small"
                                    color={employeesInShift.length > 0 ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </Box>

                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                  <strong>Timing:</strong> {formatTimeForDisplay(shift.startTime)} - {formatTimeForDisplay(shift.endTime)} (
                                  {shift.duration})
                                </Typography>

                                {employeesInShift.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      Assigned Employees:
                                    </Typography>
                                    {employeesInShift.map((employee) => (
                                      <Box
                                        key={employee.id}
                                        sx={{
                                          p: 1,
                                          bgcolor: 'background.paper',
                                          borderRadius: 1,
                                          border: '1px solid',
                                          borderColor: 'divider'
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {employee.fullName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          {employee.type} • {employee.employmentType}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    No employees assigned to this shift
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Shifts Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Substore Details</DialogTitle>
        <DialogContent>
          {editedSubstore && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Store Name"
              value={editedSubstore.storeName}
              onChange={(e) => setEditedSubstore((prev) => ({ ...prev, storeName: e.target.value }))}
              fullWidth
              sx={{ mb: 2 }}
            />
              <TextField
                label="Address"
                value={editedSubstore.address}
                onChange={(e) => setEditedSubstore((prev) => ({ ...prev, address: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Working Hours Start"
                  type="time"
                  value={editedSubstore.workingTimeStart}
                  onChange={(e) => setEditedSubstore((prev) => ({ ...prev, workingTimeStart: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  fullWidth
                />
                <TextField
                  label="Working Hours End"
                  type="time"
                  value={editedSubstore.workingTimeEnd}
                  onChange={(e) => setEditedSubstore((prev) => ({ ...prev, workingTimeEnd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  fullWidth
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveShifts} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Options Dialog */}
      <Dialog open={assignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Employee Shift Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Choose how you would like to assign employees to shifts:
            </Typography>

            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={handleAutoAssign}
              size="large"
              sx={{
                p: 3,
                justifyContent: 'flex-start',
                textAlign: 'left',
                border: '2px solid',
                borderColor: assignmentMode === 'auto' ? 'primary.main' : 'grey.300',
                bgcolor: assignmentMode === 'auto' ? 'primary.50' : 'transparent'
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Auto Assignment
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Automatically assign employees to shifts based on their availability and store requirements.
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleManualAssign}
              size="large"
              sx={{
                p: 3,
                justifyContent: 'flex-start',
                textAlign: 'left',
                border: '2px solid',
                borderColor: assignmentMode === 'manual' ? 'primary.main' : 'grey.300',
                bgcolor: assignmentMode === 'manual' ? 'primary.50' : 'transparent'
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Manual Assignment
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Manually assign each employee to specific shifts with full control over assignments.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}
